import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useDragControls } from "framer-motion";
import { 
  Minimize2, 
  Maximize2, 
  X, 
  PhoneOff, 
  Sparkles,
  Video,
  ExternalLink,
  ArrowLeft
} from "lucide-react";
import { createPortal } from "react-dom";

export const InAppCallOverlay: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [callUrl, setCallUrl] = useState<string | null>(null);
  const [patientName, setPatientName] = useState<string | null>(null);
  const [patientId, setPatientId] = useState<string | null>(null);
  const [ambientOpen, setAmbientOpen] = useState(false);

  const dragControls = useDragControls();

  useEffect(() => {
    const handleAmbientToggle = (e: Event) => {
      const customEvent = e as CustomEvent;
      setAmbientOpen(customEvent.detail?.open || false);
    };
    window.addEventListener('ambient-ai-toggled', handleAmbientToggle);
    // Initialize call active state as false on initial mount
    sessionStorage.removeItem('isCallActive');
    return () => window.removeEventListener('ambient-ai-toggled', handleAmbientToggle);
  }, []);

  useEffect(() => {
    const handleOpenCall = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        const { url, patientName: pName, patientId: pId } = customEvent.detail;
        setCallUrl(url || null);
        setPatientName(pName || null);
        setPatientId(pId || null);
        setIsOpen(true);
        setIsMinimized(false);
        sessionStorage.setItem('isCallActive', 'true');
        window.dispatchEvent(new CustomEvent('in-app-call-status', { detail: { active: true } }));
      }
    };

    window.addEventListener("open-in-app-call", handleOpenCall);
    return () => window.removeEventListener("open-in-app-call", handleOpenCall);
  }, []);

  const handleClose = () => {
    if (window.confirm("Are you sure you want to end this teleconsultation connection?")) {
      setIsOpen(false);
      setCallUrl(null);
      setPatientName(null);
      setPatientId(null);
      sessionStorage.removeItem('isCallActive');
      window.dispatchEvent(new CustomEvent('in-app-call-status', { detail: { active: false } }));
    }
  };

  const handleGoBack = () => {
    setIsOpen(false);
    setCallUrl(null);
    setPatientName(null);
    setPatientId(null);
    sessionStorage.removeItem('isCallActive');
    window.dispatchEvent(new CustomEvent('in-app-call-status', { detail: { active: false } }));
  };

  const handleOpenExternal = () => {
    if (callUrl) {
      window.open(callUrl, "_blank");
      setIsOpen(false);
      setCallUrl(null);
      setPatientName(null);
      setPatientId(null);
      sessionStorage.removeItem('isCallActive');
      window.dispatchEvent(new CustomEvent('in-app-call-status', { detail: { active: false } }));
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  if (!isOpen || !callUrl) return null;

  return createPortal(
    <AnimatePresence>
      {/* Full-Screen Mode */}
      {!isMinimized ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={`fixed inset-y-0 left-0 z-[9990] bg-slate-950 flex flex-col font-primary pointer-events-auto transition-all duration-300 ${
            ambientOpen ? "w-[calc(100%-450px)]" : "w-full"
          }`}
        >
          {/* Header Panel */}
          <div className="bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between text-white shadow-xl shrink-0 pointer-events-auto">
            <div className="flex items-center gap-4">
              <button
                onClick={handleGoBack}
                className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white font-black text-xs tracking-wider uppercase border border-slate-700/50 shadow-md transition-all active:scale-95 cursor-pointer"
                title="Go Back to Dashboard (Close View)"
              >
                <ArrowLeft size={14} className="stroke-[3]" />
                <span>Go Back</span>
              </button>

              <div className="h-6 w-px bg-slate-800 hidden sm:block" />

              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-emerald-500/20 border border-emerald-400/20 rounded-xl flex items-center justify-center">
                  <Video className="text-emerald-400 animate-pulse" size={18} />
                </div>
                <div>
                  <h3 className="text-sm font-black tracking-tight leading-none mb-1">
                    Active Video Teleconsultation
                  </h3>
                  {patientName && (
                    <p className="text-xs text-white/50 font-bold leading-none uppercase tracking-wider">
                      Patient: <span className="text-emerald-400">{patientName}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Action Suite */}
            <div className="flex items-center gap-2 pointer-events-auto">
              {/* Trigger Ambient AI directly in call */}
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('open-ambient-ai', { 
                    detail: { patientId, patientName } 
                  }));
                }}
                className="px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-black text-xs tracking-wider uppercase flex items-center gap-1.5 shadow-md shadow-violet-900/40 transition-all border border-violet-500/30"
                title="Open Ambient AI Assistant"
              >
                <Sparkles size={14} className="animate-spin-slow" />
                <span>Ambient Recorder</span>
              </button>

              <button
                onClick={handleOpenExternal}
                className="p-2.5 rounded-xl bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 border border-slate-700/50 transition-all"
                title="Open in Full Browser Tab"
              >
                <ExternalLink size={16} />
              </button>

              <button
                onClick={toggleMinimize}
                className="p-2.5 rounded-xl bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 border border-slate-700/50 transition-all"
                title="Minimize (Picture-in-Picture)"
              >
                <Minimize2 size={16} />
              </button>

              {!ambientOpen && (
                <button
                  onClick={handleClose}
                  className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-black text-xs tracking-wider uppercase flex items-center gap-1.5 shadow-md transition-all"
                  title="Disconnect Call"
                >
                  <PhoneOff size={14} />
                  <span>Disconnect</span>
                </button>
              )}
            </div>
          </div>

          {/* Iframe Viewport */}
          <div className="flex-grow bg-slate-900 relative pointer-events-auto">
            <iframe
              src={callUrl}
              allow="camera; microphone; display-capture; autoplay; clipboard-write; encrypted-media; fullscreen"
              className="w-full h-full border-none pointer-events-auto"
              title="Teleconsult Active Video Session"
            />
          </div>
        </motion.div>
      ) : (
        /* Floating Picture-in-Picture Mode */
        <motion.div
          drag
          dragMomentum={false}
          initial={{ opacity: 0, scale: 0.9, y: 100 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="fixed bottom-20 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-96 h-56 sm:h-64 z-[9990] bg-slate-950/90 backdrop-blur-2xl rounded-3xl border border-slate-800/80 hover:border-indigo-500/50 shadow-[0_0_50px_-12px_rgba(99,102,241,0.45)] hover:shadow-[0_0_50px_-6px_rgba(99,102,241,0.55)] flex flex-col overflow-hidden font-primary transition-all duration-300 cursor-grab active:cursor-grabbing select-none"
        >
          {/* PiP Header */}
          <div 
            className="bg-slate-900/85 border-b border-slate-800/60 px-4 py-2.5 flex items-center justify-between text-white select-none shrink-0"
          >
            <div className="flex items-center gap-2 truncate">
              <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-md shadow-emerald-500/50 animate-pulse shrink-0" />
              <p className="text-[10px] font-black uppercase tracking-wider truncate text-white/80">
                Call: {patientName || "Consult"}
              </p>
            </div>
            
            <div className="flex items-center gap-2 shrink-0">
              <button
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('open-ambient-ai', { 
                    detail: { patientId, patientName } 
                  }));
                }}
                className="p-1.5 rounded-lg bg-violet-500/10 text-violet-400 hover:text-white hover:bg-violet-600 border border-violet-500/10 hover:border-violet-500/30 transition-all"
                title="Open Ambient Recorder"
              >
                <Sparkles size={13} className="animate-spin-slow" />
              </button>

              <button
                onPointerDown={(e) => e.stopPropagation()}
                onClick={toggleMinimize}
                className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 border border-slate-800 hover:border-slate-700 transition-all"
                title="Expand Screen"
              >
                <Maximize2 size={13} />
              </button>

              <button
                onPointerDown={(e) => e.stopPropagation()}
                onClick={handleClose}
                className="p-1.5 rounded-lg bg-rose-500/10 text-rose-400 hover:text-white hover:bg-rose-600 border border-rose-500/10 hover:border-rose-500/30 transition-all"
                title="Disconnect Call"
              >
                <X size={13} />
              </button>
            </div>
          </div>

          {/* Mini Iframe viewport */}
          <div className="flex-grow bg-slate-900 pointer-events-auto">
            <iframe
              src={callUrl}
              allow="camera; microphone; display-capture; autoplay; clipboard-write; encrypted-media; fullscreen"
              className="w-full h-full border-none pointer-events-none"
              title="Mini Teleconsult Session"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
